import React from 'react';
import BlogLayout from '../../components/BlogLayout';

const SITE = 'https://www.roofclaw.com';
const SLUG = '/blog/roofclaw-local-service-ad-agent';
const repoUrl = 'https://github.com/Roofing-Business-Partner/Roofclaw-LocalServiceAd-Agent';

// ── FAQ Data (used for both the visible section and JSON-LD) ─────────────────
const FAQ_ITEMS = [
  {
    q: 'What is the RoofClaw Local Service Ad Agent?',
    a: 'The RoofClaw Local Service Ad Agent is an open-source MIT launchpad that connects a Sales-configured RoofClaw to Google Local Services Ads leads, HubSpot CRM, Twilio phone calling, and an xAI/Grok voice stack — targeting sub-5-minute lead response for roofing companies.',
  },
  {
    q: 'How does it respond to Google Local Services Ads leads?',
    a: 'It polls the Google Ads API every 2 minutes. When a new lead appears, it creates the contact, building/listing, and deal in HubSpot, then sends an approved first response via LSA message, phone, text, or email depending on configuration. Email notifications from Google can accelerate triggering but the API remains the source of truth.',
  },
  {
    q: 'Does it work with CRMs other than HubSpot?',
    a: 'HubSpot is the default RBP standard. Other roofing CRMs such as ProLine, RoofLink, and AccuLynx may be compatible, but each requires a compatibility proof of concept to confirm they support the required contact, property, deal, and activity logging endpoints via API.',
  },
  {
    q: 'Which phone provider does the RoofClaw Local Service Ad Agent use?',
    a: 'Twilio is the default telephony provider. CallRail is recommended when the business needs routed numbers, marketing attribution, or complex call tracking. Twilio carries the call; OpenClaw runs the agent; xAI/Grok handles the voice AI layer.',
  },
  {
    q: 'Can an existing RoofClaw use this launchpad?',
    a: 'Only if the existing RoofClaw is configured as a Sales agent, SDR, Inbound Sales, Revenue Desk, or Local Service Ad response agent. If the current RoofClaw is a Chief of Staff, Operations, Production, or Support agent, it should reject the install and a dedicated Sales RoofClaw V2.0 is recommended instead. Existing customers receive 25% off.',
  },
  {
    q: 'What AI voice stack does it use for phone calls?',
    a: 'The default is the all-xAI/Grok cascaded stack: Grok STT for speech-to-text, Grok LLM for reasoning and tool use, and Grok TTS for spoken response. ElevenLabs is optional only for custom branded voice. Grok Voice Think Fast 1.0 is documented as an upcoming path once OpenClaw support is confirmed.',
  },
  {
    q: 'Is the launchpad free to use?',
    a: 'Yes. The RoofClaw Local Service Ad Agent launchpad is published under the MIT license and is free to use with any OpenClaw-compatible Sales RoofClaw deployment.',
  },
];

// ── Structured Data ───────────────────────────────────────────────────────────
const ArticleSchema = () => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: 'RoofClaw Local Service Ad Agent: Turning Google Local Services Ads Into Booked Roofing Opportunities',
    description: 'An open-source MIT launchpad that connects a Sales-configured RoofClaw to Google LSA leads, HubSpot CRM, Twilio phone calling, and an xAI/Grok voice stack — targeting sub-5-minute lead response.',
    url: `${SITE}${SLUG}`,
    mainEntityOfPage: { '@type': 'WebPage', '@id': `${SITE}${SLUG}` },
    image: [`${SITE}/images/blog/og-lsa-agent.jpg`, `${SITE}/images/blog/roofclaw-local-service-ad-agent-hero.jpg`],
    datePublished: '2026-04-29',
    dateModified: '2026-04-29',
    inLanguage: 'en-US',
    keywords: 'Google Local Services Ads, roofing AI agent, LSA lead response, HubSpot roofing CRM, Twilio roofing, xAI Grok voice, RoofClaw, OpenClaw, roofing sales agent, AI lead response',
    articleSection: 'AI for Roofing',
    author: { '@type': 'Organization', name: 'RoofClaw', url: SITE },
    publisher: {
      '@type': 'Organization',
      name: 'RoofClaw',
      url: SITE,
      logo: { '@type': 'ImageObject', url: `${SITE}/og-image.png` },
    },
    speakable: {
      '@type': 'SpeakableSpecification',
      cssSelector: ['.article-summary', 'h1', 'h2'],
    },
  };
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />;
};

const FAQSchema = () => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQ_ITEMS.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />;
};

const BreadcrumbSchema = () => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE },
      { '@type': 'ListItem', position: 2, name: 'Blog', item: `${SITE}/blog` },
      { '@type': 'ListItem', position: 3, name: 'RoofClaw Local Service Ad Agent', item: `${SITE}${SLUG}` },
    ],
  };
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />;
};

// ── Visual Components ─────────────────────────────────────────────────────────
const TLDRBox = () => (
  <div className="not-prose article-summary my-10 rounded-2xl border border-[#10B981]/30 bg-[#0d1a14] p-7">
    <p className="text-xs font-data uppercase tracking-[0.25em] text-[#10B981] mb-4">TL;DR</p>
    <ul className="space-y-2">
      {[
        'Open-source MIT launchpad for a Sales-configured RoofClaw.',
        'Polls Google LSA leads via Google Ads API every 2 minutes — target: sub-5-min response.',
        'Creates Contact + Building/Listing + Deal in HubSpot automatically.',
        'Phone via Twilio. Voice AI via xAI/Grok (STT → LLM → TTS).',
        'Hard install guardrail: only for sales-focused SOUL.md agents.',
        'Existing RoofClaw customers: 25% off a dedicated Sales RoofClaw V2.0.',
      ].map(item => (
        <li key={item} className="flex items-start gap-3 text-[#F0EFF4]/75 text-sm leading-relaxed">
          <span className="mt-1 w-2 h-2 rounded-full bg-[#10B981] shrink-0" />
          {item}
        </li>
      ))}
    </ul>
  </div>
);

const RepoCTA = ({ compact = false }) => {
  const spacing = compact ? 'my-10' : 'my-16';
  return (
    <div className={`not-prose ${spacing} rounded-[2rem] border border-[#7B61FF]/30 bg-[#18181B] p-8 md:p-10 relative overflow-hidden`}>
      <div className="absolute inset-0 bg-gradient-to-br from-[#7B61FF]/15 via-transparent to-[#10B981]/10" />
      <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
        <div>
          <p className="text-xs font-data uppercase tracking-[0.25em] text-[#10B981] mb-3">Open-source MIT launchpad</p>
          <h3 className="text-2xl md:text-3xl font-heading font-bold text-[#F0EFF4] mb-3">Point a Sales RoofClaw at the repo.</h3>
          <p className="text-[#F0EFF4]/60 leading-relaxed max-w-2xl">
            Start with <code className="font-data text-[#7B61FF] bg-[#0A0A14] px-2 py-1 rounded">LAUNCHPAD.md</code>. If the RoofClaw is not sales-configured, it should reject the install instead of becoming five confused agents in one box.
          </p>
        </div>
        <a
          href={repoUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center rounded-full bg-[#7B61FF] px-7 py-4 font-bold text-[#F0EFF4] shadow-[0_0_24px_rgba(123,97,255,0.35)] hover:bg-[#8D78FF] transition-colors whitespace-nowrap"
        >
          View GitHub Repo →
        </a>
      </div>
    </div>
  );
};

const WorkflowImage = () => (
  <figure className="not-prose my-16 rounded-[2rem] overflow-hidden border border-[#F0EFF4]/10 bg-[#18181B] shadow-[0_0_40px_rgba(123,97,255,0.12)]">
    <img
      src="/images/blog/roofclaw-local-service-ad-agent-workflow.jpg"
      alt="RoofClaw Local Service Ad Agent workflow: Google LSA lead polling → HubSpot contact, building, deal write → Twilio phone → xAI/Grok voice → human escalation"
      className="w-full h-auto mix-blend-screen opacity-95"
    />
    <figcaption className="px-6 py-4 text-sm text-[#F0EFF4]/45 font-data border-t border-[#F0EFF4]/10">
      Google LSA lead → Sales RoofClaw → HubSpot records → approved response channel → human escalation.
    </figcaption>
  </figure>
);

const ObjectModelCards = () => (
  <div className="not-prose my-10 grid md:grid-cols-4 gap-4">
    {[
      { label: 'Contact', icon: '👤', body: 'The person: homeowner, buyer, property manager, tenant, or decision maker.' },
      { label: 'Building / Listing', icon: '🏠', body: 'The physical property. Address, service history, photos, and repeat opportunities.' },
      { label: 'Deal', icon: '📋', body: 'The sales opportunity tied to a specific building and need.' },
      { label: 'Activities', icon: '📞', body: 'Calls, notes, emails, tasks, messages, meetings, and follow-up history.' },
    ].map(({ label, icon, body }) => (
      <div key={label} className="rounded-2xl bg-[#18181B] border border-[#F0EFF4]/10 p-5">
        <div className="text-xl mb-3">{icon}</div>
        <h3 className="font-heading text-base font-bold text-[#F0EFF4] mb-2">{label}</h3>
        <p className="text-sm text-[#F0EFF4]/55 leading-relaxed">{body}</p>
      </div>
    ))}
  </div>
);

const VoiceStackDiagram = () => (
  <div className="not-prose my-10 rounded-2xl bg-[#18181B] border border-[#7B61FF]/20 p-7">
    <p className="text-xs font-data uppercase tracking-[0.25em] text-[#7B61FF] mb-6">Default Voice AI Stack</p>
    <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4">
      {[
        { label: 'Twilio', sublabel: 'Phone carrier', color: 'border-[#F0EFF4]/20 text-[#F0EFF4]/60' },
        { label: 'Grok STT', sublabel: 'Speech → Text', color: 'border-[#7B61FF]/40 text-[#7B61FF]' },
        { label: 'Grok LLM', sublabel: 'Reasoning + Tools', color: 'border-[#7B61FF]/60 text-[#7B61FF]' },
        { label: 'Grok TTS', sublabel: 'Text → Voice', color: 'border-[#7B61FF]/40 text-[#7B61FF]' },
        { label: 'HubSpot', sublabel: 'CRM write', color: 'border-[#10B981]/40 text-[#10B981]' },
      ].map(({ label, sublabel, color }, i, arr) => (
        <React.Fragment key={label}>
          <div className={`rounded-xl border px-5 py-3 text-center ${color} min-w-[100px]`}>
            <div className="font-bold text-sm font-heading">{label}</div>
            <div className="text-xs opacity-70 mt-1">{sublabel}</div>
          </div>
          {i < arr.length - 1 && (
            <span className="text-[#F0EFF4]/25 font-bold text-lg sm:text-xl select-none">→</span>
          )}
        </React.Fragment>
      ))}
    </div>
    <p className="mt-5 text-xs text-[#F0EFF4]/40 font-data">
      ElevenLabs optional for custom branded voice only. Grok Voice Think Fast 1.0 = upcoming/advanced.
    </p>
  </div>
);

const Callout = ({ type = 'info', title, children }) => {
  const styles = {
    info:    { border: 'border-[#7B61FF]/30', bg: 'bg-[#18181B]', dot: 'bg-[#7B61FF]' },
    warning: { border: 'border-amber-500/30',  bg: 'bg-[#1a1700]', dot: 'bg-amber-500' },
    success: { border: 'border-[#10B981]/30', bg: 'bg-[#0d1a14]', dot: 'bg-[#10B981]' },
  };
  const s = styles[type];
  return (
    <div className={`not-prose my-8 rounded-2xl border ${s.border} ${s.bg} p-6`}>
      {title && (
        <div className="flex items-center gap-2 mb-3">
          <span className={`w-2 h-2 rounded-full ${s.dot}`} />
          <span className="font-bold text-sm text-[#F0EFF4]/80 font-heading">{title}</span>
        </div>
      )}
      <div className="text-sm text-[#F0EFF4]/65 leading-relaxed">{children}</div>
    </div>
  );
};

const LSAIngestionSteps = () => (
  <div className="not-prose my-10 rounded-2xl bg-[#18181B] border border-[#F0EFF4]/10 p-7">
    <p className="text-xs font-data uppercase tracking-[0.25em] text-[#10B981] mb-6">LSA Lead Processing — Order of Operations</p>
    <ol className="space-y-3">
      {[
        'Poll Google Ads API for new leads (every 2 min).',
        'De-duplicate using LSA resource name / lead ID.',
        'Normalize contact and property data.',
        'Upsert HubSpot Contact.',
        'Upsert Building / Listing if address is reliable.',
        'Create or update HubSpot Deal.',
        'Log inbound LSA event as HubSpot Activity.',
        'Send approved first response (LSA message / phone / SMS / email).',
        'Log outbound response as HubSpot Activity.',
        'Create follow-up task or escalate to human.',
      ].map((step, i) => (
        <li key={i} className="flex items-start gap-4 text-[#F0EFF4]/70 text-sm leading-relaxed">
          <span className="shrink-0 w-6 h-6 rounded-full bg-[#7B61FF]/15 border border-[#7B61FF]/30 text-[#7B61FF] text-xs font-bold font-data flex items-center justify-center">{i + 1}</span>
          {step}
        </li>
      ))}
    </ol>
  </div>
);

const SoulGateTable = () => (
  <div className="not-prose my-10 rounded-2xl overflow-hidden border border-[#F0EFF4]/10">
    <div className="grid grid-cols-2">
      <div className="bg-[#0d1a14] border-b border-r border-[#F0EFF4]/10 px-6 py-4">
        <p className="text-xs font-data uppercase tracking-[0.2em] text-[#10B981] mb-3">✅ Good Fit (install)</p>
        <ul className="space-y-1.5">
          {['Sales Rep', 'SDR / Inbound Sales', 'Revenue Desk', 'Lead Qualification', 'Local Service Ad Response'].map(r => (
            <li key={r} className="text-sm text-[#F0EFF4]/70 flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[#10B981]" />{r}
            </li>
          ))}
        </ul>
      </div>
      <div className="bg-[#1a0e0e] border-b border-[#F0EFF4]/10 px-6 py-4">
        <p className="text-xs font-data uppercase tracking-[0.2em] text-red-400 mb-3">🚫 Bad Fit (reject)</p>
        <ul className="space-y-1.5">
          {['Chief of Staff', 'Production / Operations', 'Bookkeeper', 'Recruiter', 'Support / Dispatch', 'General assistant'].map(r => (
            <li key={r} className="text-sm text-[#F0EFF4]/70 flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-red-400" />{r}
            </li>
          ))}
        </ul>
      </div>
    </div>
    <div className="bg-[#18181B] border-t border-[#F0EFF4]/10 px-6 py-4 text-xs text-[#F0EFF4]/40 font-data">
      Gate check runs against <code className="text-[#7B61FF]">SOUL.md</code> at install time. If the gate rejects, the agent explains why and recommends a dedicated Sales RoofClaw V2.0.
    </div>
  </div>
);

const FAQSection = () => (
  <section aria-label="Frequently Asked Questions" className="not-prose mt-20 mb-8">
    <h2 className="text-3xl font-heading font-bold text-[#F0EFF4] mb-8">Frequently Asked Questions</h2>
    <dl className="space-y-6">
      {FAQ_ITEMS.map(({ q, a }) => (
        <div key={q} className="rounded-2xl bg-[#18181B] border border-[#F0EFF4]/10 p-6">
          <dt className="font-heading font-bold text-[#F0EFF4] mb-3">{q}</dt>
          <dd className="text-[#F0EFF4]/65 leading-relaxed text-sm">{a}</dd>
        </div>
      ))}
    </dl>
  </section>
);

// ── Main Page Component ───────────────────────────────────────────────────────
const RoofClawLocalServiceAdAgent = () => (
  <BlogLayout
    title="RoofClaw Local Service Ad Agent: Turning Google Local Services Ads Into Booked Roofing Opportunities"
    date="April 29, 2026"
    readTime={8}
    heroImage="/images/blog/roofclaw-local-service-ad-agent-hero.jpg"
    description="An open-source MIT launchpad that connects a Sales-configured RoofClaw to Google LSA leads, HubSpot CRM, Twilio phone calling, and an xAI/Grok voice stack — targeting sub-5-minute response."
    canonicalPath={SLUG}
    keywords="Google Local Services Ads, roofing AI agent, LSA lead response, HubSpot roofing, Twilio roofing, xAI Grok voice, RoofClaw, OpenClaw"
    listItems={[
      'Google Local Services Ads lead response in under 5 minutes',
      'HubSpot CRM hygiene: Contact, Building, Deal, Activities',
      'Twilio-first phone calling with xAI/Grok voice stack',
      'Sales-only SOUL.md install guardrail',
      'Open-source MIT license',
    ]}
  >
    {/* ── Structured Data ── */}
    <ArticleSchema />
    <FAQSchema />
    <BreadcrumbSchema />

    {/* ── Breadcrumb ── */}
    <nav aria-label="Breadcrumb" className="not-prose mb-8 text-xs text-[#F0EFF4]/35 font-data flex items-center gap-2">
      <a href={SITE} className="hover:text-[#7B61FF] transition-colors">Home</a>
      <span>/</span>
      <a href={`${SITE}/blog`} className="hover:text-[#7B61FF] transition-colors">Blog</a>
      <span>/</span>
      <span className="text-[#F0EFF4]/55">RoofClaw Local Service Ad Agent</span>
    </nav>

    {/* ── Lede ── */}
    <p className="lead text-xl text-[#F0EFF4] font-medium">
      Roofing companies do not usually lose good leads because nobody cares.
    </p>
    <p>
      They lose them because the lead arrives while the owner is on a roof, the sales manager is in a truck, the office is juggling three other conversations, and the CRM gets updated later — if it gets updated at all.
    </p>
    <p>
      That delay is expensive. A homeowner requesting help through Google Local Services Ads is already raising their hand. If your company is slow, disorganized, or unclear in the first response, the next contractor has a chance to win the conversation before your team even sees it.
    </p>

    {/* ── TL;DR ── */}
    <TLDRBox />

    {/* ── Repo CTA ── */}
    <RepoCTA compact />

    {/* ── What it does ── */}
    <h2>What Does the RoofClaw Local Service Ad Agent Do?</h2>
    <p>
      The <strong>RoofClaw Local Service Ad Agent</strong> is an open-source MIT launchpad that a properly configured Sales RoofClaw can use to respond to Google Local Services Ads leads, keep HubSpot clean, make approved phone calls through Twilio, and run an xAI/Grok-powered voice stack for live lead intake.
    </p>
    <p>When fully implemented, a Sales RoofClaw can:</p>
    <ul>
      <li>Respond to Google Local Services Ads leads in under 5 minutes.</li>
      <li>Create or update the right Contact, Building/Listing, and Deal records in HubSpot.</li>
      <li>Reply through approved channels: LSA message, phone, text/iMessage, or email.</li>
      <li>Log every call, note, message, task, and follow-up activity.</li>
      <li>Escalate pricing, legal, insurance, angry-caller, or unclear situations to a human.</li>
    </ul>

    <WorkflowImage />

    {/* ── HubSpot Standard ── */}
    <h2>Why HubSpot — and How It Models Roofing Data</h2>
    <p>
      HubSpot is the default because it gives a roofing company a clean, composable CRM model. The launchpad is built around four objects:
    </p>
    <ObjectModelCards />
    <p>
      That separation matters. A contact may own multiple properties. A property may have multiple opportunities over time. If everything gets jammed into one contact note, the CRM turns into a junk drawer.
    </p>

    <Callout type="success" title="Why separate Building from Contact?">
      A photo folder or property file should survive beyond one deal. One building can have an inspection deal, repair deal, replacement deal, warranty deal, and future storm deal — all over time. Tying property-level context only to a contact or deal loses that history.
    </Callout>

    <p>
      Other roofing CRMs — ProLine/Powrline, RoofLink, AccuLynx — may support similar outcomes, but they require a compatibility proof of concept first. The agent should not fake a CRM integration it cannot actually support.
    </p>

    {/* ── LSA Ingestion ── */}
    <h2>How Does Google LSA Lead Ingestion Work?</h2>
    <p>
      The default ingestion path is <strong>Google Ads API polling every 2 minutes</strong>. That is intentional.
    </p>
    <p>
      The API is the source of truth. Email notifications from Google can accelerate triggering, but they should not be treated as the canonical lead record. Emails can be delayed, filtered, or incomplete.
    </p>

    <Callout type="info" title="Why 2 minutes and not 1?">
      A 2-minute polling interval gives enough margin for API/network delay, HubSpot writes, and first-response assembly — while keeping the agent responsive. One-minute HEARTBEAT loops are not recommended; use a durable OpenClaw cron job instead.
    </Callout>

    <LSAIngestionSteps />

    {/* ── Phone ── */}
    <h2>Which Phone Provider Should You Use?</h2>
    <p>
      The decision is straightforward:
    </p>
    <ul>
      <li><strong>Use Twilio</strong> when the RoofClaw can have a dedicated sales number, the number primarily belongs to the agent, and the business does not need complex routing or attribution.</li>
      <li><strong>Use CallRail</strong> when the business needs routed numbers, call tracking, LSA/office-number complexity, attribution, or campaign-level number pools.</li>
    </ul>
    <p>
      Start with Twilio. Add CallRail when the business case requires it. Customers who need CallRail can use the RBP affiliate link for setup support.
    </p>

    {/* ── Voice Stack ── */}
    <h2>What AI Voice Stack Does It Use?</h2>
    <p>
      The default voice AI path is the all-xAI/Grok cascaded stack:
    </p>
    <VoiceStackDiagram />
    <p>
      ElevenLabs is optional — only for custom branded voice polish. Grok Voice Think Fast 1.0 is an upcoming/advanced path once OpenClaw registers first-class support; it is not the normal customer self-setup path yet.
    </p>

    {/* ── Phone Mode Privacy ── */}
    <h2>Why Does Customer Phone Mode Need to Be Narrow?</h2>
    <p>
      Phone calls are higher risk than text because the agent speaks live to a real person. The Sales RoofClaw should not bring its full memory, internal notes, owner conversations, or non-sales tooling into a customer call.
    </p>
    <p><strong>Customer Phone Mode should only access:</strong></p>
    <ul>
      <li>Approved sales script and call guardrails.</li>
      <li>Company-safe FAQ.</li>
      <li>Current lead, contact, deal, and building context.</li>
      <li>HubSpot lookup and write actions needed for the call.</li>
      <li>Calendar or booking rules, if explicitly connected.</li>
      <li>Human escalation rules.</li>
    </ul>
    <Callout type="warning" title="Keep the phone prompt narrow">
      The phone agent's job is to help the lead take the next safe step — not improvise from every thought the company has ever written down. Prompt separation between SOUL.md and the phone-specific response prompt is required.
    </Callout>

    {/* ── Soul Gate ── */}
    <h2>When Should You NOT Use This Launchpad?</h2>
    <p>
      The launchpad includes a hard gate that checks the RoofClaw's <code>SOUL.md</code> before installation. Here is what passes and what gets rejected:
    </p>
    <SoulGateTable />
    <p>
      If the existing RoofClaw is not sales-configured, the agent rejects the install and recommends a dedicated <strong>Sales RoofClaw V2.0</strong>. Existing customers receive <strong>25% off</strong>.
    </p>
    <p>
      If the business is not ready for a second RoofClaw yet, the right move is to make the first one so economically valuable that the second one becomes obvious.
    </p>

    {/* ── Setup ── */}
    <h2>What Do You Need Before Going Live?</h2>
    <ul>
      <li>A Sales-configured RoofClaw with a clearly sales-focused <code>SOUL.md</code>.</li>
      <li>HubSpot access with object mapping confirmed.</li>
      <li>Google Ads / Local Services Ads API access.</li>
      <li>Approved first-response scripts and clear escalation rules.</li>
      <li>Twilio account and phone number (if phone is enabled).</li>
      <li>xAI API access for Grok STT, LLM, and TTS.</li>
      <li>Call recording, AI disclosure, TCPA/CASL compliance reviewed by the business.</li>
      <li>Dry-run testing before real leads are processed.</li>
      <li>Human review for the first 10 live calls and responses.</li>
    </ul>
    <p>
      The launchpad is intentionally conservative. It proves the data path, proves the response path, proves the escalation path — then moves into live mode carefully.
    </p>

    {/* ── Why it matters ── */}
    <h2>The Bottom Line</h2>
    <p>
      A roofing business does not need more software clutter. It needs fewer dropped balls.
    </p>
    <p>
      When a Local Services Ads lead comes in, the company needs a fast, professional first response. It needs the CRM updated. It needs the property tied to the right person and opportunity. It needs follow-up tasks created. It needs a human brought in when the situation is sensitive.
    </p>
    <p>
      It is not magic. It is disciplined speed.
    </p>
    <p>
      A dedicated Sales RoofClaw, pointed at the right launchpad, with the right guardrails, gives a roofing business a cleaner revenue desk without asking the owner to become the CRM janitor.
    </p>

    {/* ── Bottom CTA ── */}
    <RepoCTA />

    {/* ── FAQ ── */}
    <FAQSection />
  </BlogLayout>
);

export default RoofClawLocalServiceAdAgent;
